/* eslint-disable */
import axios from 'axios';
import { isNil } from 'lodash';
import { ACCOUNT_API_URL } from '../lib/environment.js';
import { getAccess } from './modules/access.js';

// One message for every credential failure (bad password, unknown identifier,
// no login access) so a response never says which part was wrong.
export const INVALID_LOGIN_MESSAGE = 'Invalid Email/User ID or password.';
// Shown for anything that is not a credential failure (network down, 404/5xx from the
// Accounts service) so the raw axios text never reaches the banner.
export const SERVICE_ERROR_MESSAGE = 'Login is unavailable right now. Please try again.';

// Mirrors office: the identifier is trimmed and otherwise sent as typed, so an
// Office User ID or an email both reach the Accounts service unchanged.
export const normalizeLoginIdentifier = (value) =>
	isNil(value) ? '' : String(value).trim();

export default {
	login: async (opts) => {
		let timer = null;

		return new Promise((resolve, reject) => {
			const { password } = opts;
			const username = normalizeLoginIdentifier(opts.username);

			if (username === '') {
				resolve({ status: 1, message: INVALID_LOGIN_MESSAGE });
				return;
			}

			const loginController = new AbortController();

			timer = setTimeout(() => {
				loginController.abort();
			}, 180000);

			axios({
				url: `${ACCOUNT_API_URL}/GET/logins/AUTHZ/users`,
				method: 'POST',
				cache: 'no-cache',
				headers: {
					'Content-Type': 'application/json',
				},
				data: {
					username: username,
					password: password,
				},
				signal: loginController.signal,
			})
				.then((resp) => {
					clearTimeout(timer);

					if (resp.data.result.length === 0) {
						resolve({ status: 1, message: INVALID_LOGIN_MESSAGE });
						return;
					}

					if (resp.data.result.length > 1) {
						resolve({
							status: 1,
							message:
								'This user has multiple accounts, and cannot login at this time.',
						});
						return;
					}

					const sessionController = new AbortController();

					timer = setTimeout(() => {
						sessionController.abort();
					}, 180000);

					return axios({
						url: `${ACCOUNT_API_URL}/sessions`,
						method: 'POST',
						cache: 'no-cache',
						headers: {
							'Content-Type': 'application/json',
						},
						data: {
							user: resp.data.result[0]._id,
							password: password,
						},
						signal: sessionController.signal,
					});
				})
				.then((resp) => {
					clearTimeout(timer);

					let access;

					if (!isNil(resp.data.result.session.user)) {
						access = getAccess(resp.data.result.session.user);
					}

					if (!access || !access.canLogin) {
						resolve({ status: 1, message: INVALID_LOGIN_MESSAGE });
					} else {
						resolve(resp.data);
					}
				})
				.catch((err) => {
					if (err.response?.status === 401) {
						resolve({ status: 1, message: INVALID_LOGIN_MESSAGE });
						return;
					}

					clearTimeout(timer);

					console.error('login error', err);
					reject({ status: 1, message: SERVICE_ERROR_MESSAGE });
				});
		});
	},
	refresh: async () => {
		return new Promise((resolve, reject) => {
			const controller = new AbortController();

			let timeout = setTimeout(() => {
				controller.abort();
			}, 180000);

			axios({
				url: `${ACCOUNT_API_URL}/sessions/CURRENT`,
				method: 'GET',
				cache: 'no-cache',
				headers: {
					'Content-Type': 'application/json',
				},
				signal: controller.signal,
			})
				.then((resp) => {
					clearTimeout(timeout);

					let access;

					if (!isNil(resp.data.result.session.user)) {
						access = getAccess(resp.data.result.session.user);
					}

					// Resolved, not rejected: the Router logs out on a non-zero status but
					// swallows a rejection, which would leave a non-admin session on a dead page.
					if (!access || !access.canLogin) {
						resolve({ status: 1, message: INVALID_LOGIN_MESSAGE });
					} else {
						resolve(resp.data);
					}
				})
				.catch((err) => {
					clearTimeout(timeout);

					reject({ status: 1, message: err.message });
				});
		});
	},
};
